import { useState } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { SearchForm } from '@/components/prospects/SearchForm';
import { ProspectResults } from '@/components/prospects/ProspectResults';
import { SearchHistory } from '@/components/prospects/SearchHistory';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SearchCriteria {
  jobTitle: string;
  location: string;
  locationType: 'exact' | 'radius';
  radius: number;
  companyName?: string;
  industry?: string;
  companySize?: string;
  keywordsInName?: string;
  keywordTags?: string;
  hasEmail?: boolean;
  hasPhone?: boolean;
  enrichEmails?: boolean;
}

export interface Prospect {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  title: string;
  linkedinUrl: string;
  industry: string;
  location: string;
}

const People = () => {
  const [searchResults, setSearchResults] = useState<Prospect[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSearch, setCurrentSearch] = useState<SearchCriteria | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);

  const handleSearch = async (criteria: SearchCriteria) => {
    setIsSearching(true);
    setCurrentSearch(criteria);
    setCurrentPage(1);
    
    try {
      const { data, error } = await supabase.functions.invoke('apollo-search', {
        body: { ...criteria, page: 1 },
      });

      if (error) throw error;

      setSearchResults(data.prospects || []);
      setTotalResults(data.total || 0);
      
      if (data.creditsRemaining !== null && data.creditsRemaining !== undefined) {
        setCreditsRemaining(data.creditsRemaining);
      }

      toast.success(`Found ${data.total || 0} prospects`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search prospects. Please check your API key and try again.');
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = async (page: number) => {
    if (!currentSearch) return;
    
    setCurrentPage(page);
    setIsSearching(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('apollo-search', {
        body: { ...currentSearch, page },
      });

      if (error) throw error;

      setSearchResults(data.prospects || []);
      setCurrentPage(page);
      
      if (data.creditsRemaining !== null && data.creditsRemaining !== undefined) {
        setCreditsRemaining(data.creditsRemaining);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Page change error:', error);
      toast.error('Failed to load page');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-gray-50">
        <AppSidebar />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold text-gray-900">Find People</h1>
              </div>
              <p className="text-gray-600">Search for prospects using Apollo.io's comprehensive database</p>
            </div>

            <Tabs defaultValue="search" className="space-y-6">
              <TabsList>
                <TabsTrigger value="search">Search</TabsTrigger>
                <TabsTrigger value="history">Search History</TabsTrigger>
              </TabsList>

                <TabsContent value="search" className="space-y-6">
                  <SearchForm 
                    onSearch={handleSearch} 
                    isSearching={isSearching}
                    creditsRemaining={creditsRemaining}
                  />

                {searchResults.length > 0 && (
                  <ProspectResults
                    prospects={searchResults}
                    totalResults={totalResults}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    searchCriteria={currentSearch}
                  />
                )}

                {!isSearching && searchResults.length === 0 && currentSearch && (
                  <div className="text-center py-12 bg-white rounded-lg border shadow-sm">
                    <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-600">Try adjusting your search criteria</p>
                  </div>
                )}

                {!isSearching && !currentSearch && (
                  <div className="text-center py-12 bg-white rounded-lg border shadow-sm">
                    <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Start your search</h3>
                    <p className="text-gray-600">Enter your search criteria above to find prospects</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history">
                <SearchHistory onRerunSearch={handleSearch} />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default People;
